import { FilterOperator } from "domain/asset-management/assetFilterConstants";
import { AssetPathKey } from "domain/asset-management/assetPathMapping";
import { AssetFilter } from "services/asset-management/AssetService";
import { toUtcEndOfDay, toUtcStartOfDay } from "utils/format";

export type AssetCustomViewOperator = FilterOperator;

export interface AssetCustomViewCondition {
    field: string;
    operator: AssetCustomViewOperator;
    value: string;
    logicOperator: "AND" | "OR";
}

export interface AssetCustomViewConditionGroup {
    conditions: AssetCustomViewCondition[];
    logicOperator: "AND" | "OR";
    entryLogicOperator?: "AND" | "OR";
}

export type AssetCustomViewFilterEntry = AssetCustomViewCondition | AssetCustomViewConditionGroup;

export const toFilterEntryArray = (entries: unknown): AssetCustomViewFilterEntry[] => {
    if (Array.isArray(entries)) {
        return entries;
    }
    if (entries && typeof entries === "object" && Array.isArray((entries as { filters?: unknown }).filters)) {
        return (entries as { filters: AssetCustomViewFilterEntry[] }).filters;
    }
    return [];
};

export const isDateEntry = (entry: AssetCustomViewFilterEntry, fieldName: string): entry is AssetCustomViewCondition =>
    "field" in entry && entry.field === fieldName;

export const isStartDateEntry = (entry: AssetCustomViewFilterEntry): entry is AssetCustomViewCondition =>
    isDateEntry(entry, "start_date");

export const isEndDateEntry = (entry: AssetCustomViewFilterEntry): entry is AssetCustomViewCondition =>
    isDateEntry(entry, "end_date");

const extractDateRangeFromFilters = (
    filters: AssetCustomViewFilterEntry[]
): {
    startDate?: string;
    endDate?: string;
    remainingFilters: AssetCustomViewFilterEntry[];
} => {
    const startEntry = filters.find((filter) => isStartDateEntry(filter));
    const endEntry = filters.find((filter) => isEndDateEntry(filter));
    const otherFilters = filters.filter((entry) => entry !== startEntry && entry !== endEntry);
    return {
        startDate: startEntry ? toUtcStartOfDay(new Date(startEntry.value)) : undefined,
        endDate: endEntry ? toUtcEndOfDay(new Date(endEntry.value)) : undefined,
        remainingFilters: otherFilters,
    };
};

/**
 * Put a date range back into the filters the way the backend stores it. Inverse of
 * extractDateRangeFromFilters, which hoists the range out of the filters when a view is read.
 */
export const withDateRangeFilters = (
    filters: AssetCustomViewFilterEntry[],
    startDate?: string,
    endDate?: string
): AssetCustomViewFilterEntry[] => {
    const filtersWithoutDateRange = filters.filter((entry) => !isStartDateEntry(entry) && !isEndDateEntry(entry));
    if (!startDate || !endDate) {
        return filtersWithoutDateRange;
    }
    return [
        ...filtersWithoutDateRange,
        { field: "start_date", operator: FilterOperator.EQUAL, value: startDate, logicOperator: "AND" },
        { field: "end_date", operator: FilterOperator.EQUAL, value: endDate, logicOperator: "AND" },
    ];
};

export interface AssetCustomViewDto {
    uuid: string;
    name: string;
    columns: string[];
    folder: boolean;
    is_group?: boolean;
    filters?: AssetCustomViewFilterEntry[] | { filters: AssetCustomViewFilterEntry[] };
    parent_uuid: string | null;
    children_views?: AssetCustomViewDto[];
    archived?: boolean;
    description?: string;
}

export interface CreateAssetCustomView {
    uuid: string;
    name: string;
    columns: string[];
    folder: boolean;
    filters?: AssetCustomViewFilterEntry[] | { filters: AssetCustomViewFilterEntry[] };
    parentUuid?: string;
    description?: string;
}

export interface AssetCustomView {
    uuid: string;
    name: string;
    columns: string[];
    folder: boolean;
    isGroup?: boolean;
    filters?: AssetCustomViewFilterEntry[] | { filters: AssetCustomViewFilterEntry[] };
    parentUuid?: string;
    childrenViews: AssetCustomView[];
    isArchived?: boolean;
    description?: string;
    startDate?: string;
    endDate?: string;
}

export interface CreateAssetCustomViewDto {
    name: string;
    columns: string[];
    folder: boolean;
    filters?: AssetCustomViewFilterEntry[] | { filters: AssetCustomViewFilterEntry[] };
    parent_uuid?: string;
    description?: string;
}

export function toCreateAssetViewDto(view: CreateAssetCustomView): CreateAssetCustomViewDto {
    return {
        columns: view.columns,
        filters: view.filters,
        folder: view.folder,
        name: view.name.trim(),
        parent_uuid: view.parentUuid,
        description: view.description,
    };
}

export function toAssetCustomView(dto: AssetCustomViewDto): AssetCustomView {
    const { startDate, endDate, remainingFilters } = extractDateRangeFromFilters(toFilterEntryArray(dto.filters));
    return {
        childrenViews: dto.children_views ? dto.children_views.map(toAssetCustomView) : [],
        columns: dto.columns,
        filters: remainingFilters,
        folder: dto.folder,
        isGroup: dto.is_group ?? false,
        name: dto.name,
        uuid: dto.uuid,
        parentUuid: dto.parent_uuid ?? undefined,
        isArchived: dto.archived ?? false,
        description: dto.description ?? undefined,
        startDate,
        endDate,
    };
}

export interface Assets {
    assetId: string;
    serialNumber: string;
    assetType: string;
    assetTag: string;
    manufacturer: string;
    model: string;
    assetState: string;
    assignedTo: string;
    location: string;
    department: string;
    region: string;
    assetDetails: AssetDetails[];
}

export interface AssetDetails {
    key: string;
    value: string;
}

export const MAX_IMPORT_FILE_SIZE_MEGABYTES = 5;
export const MAX_IMPORT_FILE_SIZE_BYTES = MAX_IMPORT_FILE_SIZE_MEGABYTES * 1024 * 1024;

export const ASSET_OVERVIEW = [
    AssetPathKey.ASSET_ID,
    AssetPathKey.ASSET_TYPE,
    AssetPathKey.SERIAL_NUMBER,
    AssetPathKey.ASSIGNED_TO,
    AssetPathKey.ASSET_STATE,
    AssetPathKey.MANUFACTURER,
    AssetPathKey.MODEL,
    AssetPathKey.MODEL_NUMBER,
    AssetPathKey.ASSET_TAG,
    AssetPathKey.LOCATION,
    AssetPathKey.DEPARTMENT,
    AssetPathKey.REGION,
    AssetPathKey.PARENT_ASSET_ID,
    AssetPathKey.BLANCCO_ERASABILITY,
    AssetPathKey.BLANCCO_DURATION,
];

export const WARRANTY_STATUS = [
    AssetPathKey.WARRANTY_PROVIDER,
    AssetPathKey.WARRANTY_STATUS,
    AssetPathKey.WARRANTY_EXPIRY_DATE,
];

export const ASSET_RISK = [AssetPathKey.VULNERABILITY_FLAGS, AssetPathKey.REGULATORY_REQUIREMENTS];

export const ASSET_LIFECYCLE = [
    AssetPathKey.LIFECYCLE_STAGE_STATUS,
    AssetPathKey.EOL,
    AssetPathKey.EOS,
    AssetPathKey.DISPOSAL_DATE,
    AssetPathKey.LAST_AUDIT_DATE,
    AssetPathKey.FIRST_DISCOVERED,
    AssetPathKey.LAST_CONTACT,
    AssetPathKey.USAGE_TYPE,
];

export const ASSET_RISK_AND_LIFECYCLE = [...ASSET_RISK, ...ASSET_LIFECYCLE];

export const ASSET_IDENTITY_AND_CLASSIFICATION = [
    AssetPathKey.UUID,
    AssetPathKey.HOST_NAME,
    AssetPathKey.FQDN,
    AssetPathKey.DOMAIN,
    AssetPathKey.WORKGROUP,
    AssetPathKey.OPERATING_SYSTEM,
    AssetPathKey.DISCOVERY_SOURCE,
    AssetPathKey.CREATED_AT,
    AssetPathKey.CREATED_BY,
    AssetPathKey.UPDATED_AT,
    AssetPathKey.UPDATED_BY,
];

export const HARDWARE_SPECIFICATIONS = [
    AssetPathKey.CPU_MODEL,
    AssetPathKey.CPU_CORE_COUNT,
    AssetPathKey.CPU_CLOCK_SPEED,
    AssetPathKey.TOTAL_RAM,
    AssetPathKey.MEMORY_GB,
    AssetPathKey.SYSTEM_ARCHITECTURE,
];

export const STORAGE = [
    AssetPathKey.DISK_SPACE,
    AssetPathKey.DISK_TYPE,
    AssetPathKey.STORAGE_RAM,
    AssetPathKey.OS_DISK_TYPE,
];

export const NETWORK_AND_IDENTITY = [
    AssetPathKey.MAC_ADDRESS,
    AssetPathKey.IP_ADDRESS,
    AssetPathKey.CHASSIS_TYPE,
    AssetPathKey.SYSTEM_SERIAL_NUMBER,
];

export const BATTERY = [
    AssetPathKey.BATTERY_CYCLE_COUNT,
    AssetPathKey.BATTERY_DESIGNED_CAPACITY,
    AssetPathKey.BATTERY_FULL_CHARGE_CAPACITY,
    AssetPathKey.BATTERY_HEALTH,
    AssetPathKey.BATTERY_MANUFACTURE_DATE,
];

export const AUTOPILOT = [
    AssetPathKey.AZURE_AD_DEVICE_ID,
    AssetPathKey.GROUP_TAG,
    AssetPathKey.DEPLOYMENT_PROFILE,
    AssetPathKey.AUTOPILOT_ENROLLMENT_STATE,
    AssetPathKey.ENROLLMENT_TYPE,
    AssetPathKey.ENROLLMENT_DATE_TIME,
    AssetPathKey.DEFENDER_ONBOARDING_STATUS,
    AssetPathKey.DEVICE_OWNERSHIP,
    AssetPathKey.LSA_CONFIGURATION_STATE,
    AssetPathKey.MANUFACTURER_ID,
    AssetPathKey.UEFI_SETTINGS_NAME,
];

export const SECURITY_AND_ENDPOINT_HEALTH = [
    AssetPathKey.DEFENDER_SENSOR_HEALTH,
    AssetPathKey.MDE_DEVICE_ID,
    AssetPathKey.COMPLIANCE,
    AssetPathKey.ACTIVATION_LOCK_ENABLED,
    AssetPathKey.FIND_MY_IPHONE,
];

export const FINANCIAL_AND_PROCUREMENT = [
    AssetPathKey.ACQUISITION_DATE,
    AssetPathKey.PURCHASE_ORDER_ID,
    AssetPathKey.COST,
    AssetPathKey.DEPRECIATION,
    AssetPathKey.CURRENCY,
    AssetPathKey.CONTRACT,
    AssetPathKey.LEASE,
];

export const GRADING_ACTION_DETAIL_KEY = "grading_action";

export const GRADING_ACTION_KEYS = ["REUSE", "REPAIR", "RESELL", "PART_HARVEST", "RECYCLE", "DONATE"] as const;

export type GradingAction = (typeof GRADING_ACTION_KEYS)[number];

// Retired actions. Assets may still hold them, so they are shown but never offered for selection or accepted for saving.
export const LEGACY_GRADING_ACTION_KEYS = [
    "reuseInternally",
    "sell",
    "quarantine",
    "eraseInHouse",
    "sendToItad",
    "improveAndSell",
] as const;

export type LegacyGradingAction = (typeof LEGACY_GRADING_ACTION_KEYS)[number];

export type StoredGradingAction = GradingAction | LegacyGradingAction;

const GRADING_ACTION_TRANSLATION_PREFIX = "AssetManagement.value.gradingTable";

export interface GradingActionOption {
    value: StoredGradingAction;
    translationKey: string;
}

export function toGradingAction(value: unknown): GradingAction | undefined {
    return typeof value === "string" ? GRADING_ACTION_KEYS.find((action) => action === value) : undefined;
}

export function toLegacyGradingAction(value: unknown): LegacyGradingAction | undefined {
    return typeof value === "string" ? LEGACY_GRADING_ACTION_KEYS.find((action) => action === value) : undefined;
}

/** Parse a stored value for display. Use {@link toGradingAction} when validating a value to be saved. */
export function toStoredGradingAction(value: unknown): StoredGradingAction | undefined {
    return toGradingAction(value) ?? toLegacyGradingAction(value);
}

/** List the selectable actions, plus the current value when it is a retired action so that it can be displayed. */
export function getGradingActionOptions(currentValue: unknown): GradingActionOption[] {
    const options: GradingActionOption[] = GRADING_ACTION_KEYS.map((key) => ({
        value: key,
        translationKey: `${GRADING_ACTION_TRANSLATION_PREFIX}.actions.${key}`,
    }));
    const legacyAction = toLegacyGradingAction(currentValue);
    if (legacyAction === undefined) {
        return options;
    }
    return [
        ...options,
        { value: legacyAction, translationKey: `${GRADING_ACTION_TRANSLATION_PREFIX}.legacyActions.${legacyAction}` },
    ];
}

export enum AssetEventAction {
    ASSET_ENTRY_ADDED = "ASSET_ENTRY_ADDED",
    ASSET_ENTRY_EDITED = "ASSET_ENTRY_EDITED",
    ASSET_ENTRY_DELETED = "ASSET_ENTRY_DELETED",
}

export type LogicOperator = "AND" | "OR";

export const mapCustomViewFiltersToApiFilters = (entries: unknown): AssetFilter[] => {
    const filterEntries = toFilterEntryArray(entries);
    const result: AssetFilter[] = [];
    filterEntries.forEach((entry) => {
        if ("field" in entry) {
            result.push({ name: entry.field, value: entry.value, operator: entry.operator });
        } else {
            entry.conditions.forEach((condition) => {
                result.push({ name: condition.field, value: condition.value, operator: condition.operator });
            });
        }
    });
    return result;
};

export function buildViewFilterLogic(entries: unknown, startIndex: number): string {
    const filterEntries = toFilterEntryArray(entries);

    if (filterEntries.length === 0) {
        return "";
    }

    let currentIndex = startIndex;
    let result = "";

    for (const entry of filterEntries) {
        if ("field" in entry) {
            const part = String(currentIndex++);
            result = result === "" ? part : `${result} ${entry.logicOperator} ${part}`;
        } else {
            const groupParts: string[] = [];
            entry.conditions.forEach((condition, conditionIndex) => {
                const conditionPart = String(currentIndex++);
                groupParts.push(conditionIndex === 0 ? conditionPart : `${condition.logicOperator} ${conditionPart}`);
            });
            const groupLogic = groupParts.length > 1 ? `(${groupParts.join(" ")})` : (groupParts[0] ?? "");
            const entryOperator = entry.entryLogicOperator ?? "AND";
            result = result === "" ? groupLogic : `${result} ${entryOperator} ${groupLogic}`;
        }
    }

    return result;
}

export interface ViewFilterSelection {
    filters: AssetFilter[];
    filterLogic?: string;
}

const toFilterSelectionWithoutDateRange = (entries: unknown): ViewFilterSelection => {
    const filters = mapCustomViewFiltersToApiFilters(entries);
    if (filters.length <= 1) {
        return { filters };
    }
    return { filters, filterLogic: buildViewFilterLogic(entries, 0) || undefined };
};

export const toViewFilterSelection = (entries: unknown, startDate?: string, endDate?: string): ViewFilterSelection => {
    const selection = toFilterSelectionWithoutDateRange(entries);
    const dateRangeFilters = mapCustomViewFiltersToApiFilters(withDateRangeFilters([], startDate, endDate));
    if (dateRangeFilters.length === 0) {
        return selection;
    }
    const dateRangeIndexes = dateRangeFilters.map((_, index) => String(selection.filters.length + index));
    return {
        filters: [...selection.filters, ...dateRangeFilters],
        filterLogic: selection.filterLogic && [`(${selection.filterLogic})`, ...dateRangeIndexes].join(" AND "),
    };
};

export interface AssetViewScope extends ViewFilterSelection {
    groupUuid: string;
    startDate?: string;
    endDate?: string;
}

export const toAssetViewScope = (view: AssetCustomView): AssetViewScope => ({
    groupUuid: view.uuid,
    ...toViewFilterSelection(view.filters),
    startDate: view.startDate,
    endDate: view.endDate,
});

export function buildFilterLogic(nonViewCount: number, viewFilters: unknown, totalFilters: number): string | undefined {
    if (totalFilters <= 1) {
        return undefined;
    }
    const nonViewLogic = Array.from({ length: nonViewCount }, (_, index) => String(index)).join(" AND ");
    const viewCount = totalFilters - nonViewCount;
    const viewLogic =
        buildViewFilterLogic(viewFilters ?? [], nonViewCount) || (viewCount === 1 ? String(nonViewCount) : "");
    const wrappedViewLogic = viewCount > 1 ? `(${viewLogic})` : viewLogic;
    const logicParts = [nonViewLogic, wrappedViewLogic].filter(Boolean);
    return logicParts.length > 0 ? logicParts.join(" AND ") : undefined;
}

export const TRACKED_ASSETS_LIMIT_PERCENTAGE = 10;

export function isLimitApproaching(remainingSlots: number, maxAllowedLimitForTenant: number) {
    const approachingLimit = Math.ceil(maxAllowedLimitForTenant / TRACKED_ASSETS_LIMIT_PERCENTAGE);
    return remainingSlots > 0 && remainingSlots <= approachingLimit;
}

export type AssetErasureHistorySource = "blancco" | "custom";

export const SUCCESSFUL_ERASURE = "SUCCESSFUL";
export const FAILED_ERASURE = "FAILED";
export const UNKNOWN_ERASURE = "UNKNOWN";
export const CANCELLED_ERASURE = "CANCELLED";
export const DIAGNOSED_ERASURE = "DIAGNOSED";

export interface AssetErasureHistory {
    reportUuid: string;
    erasureDate: string;
    erasureStatus: string;
    source: AssetErasureHistorySource;
}

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;

export const formatDurationSeconds = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR);
    const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
    const seconds = totalSeconds % SECONDS_PER_MINUTE;
    const parts: string[] = [];
    if (hours > 0) {
        parts.push(`${hours}h`);
    }
    if (minutes > 0) {
        parts.push(`${minutes}m`);
    }
    if (seconds > 0 || parts.length === 0) {
        parts.push(`${seconds}s`);
    }
    return parts.join(" ");
};
